import { Actions } from 'vuex-smart-module';
import { ProjectsService } from '@/modules/projects/services';
import { Filters, FiltersPagination } from '@/types';
import { ProjectToCartRequest } from '@/modules/projects/types';
import ProjectDetailsGetters from './getters';
import ProjectDetailsMutations from './mutations';
import ProjectDetailsState from './state';

const projectsService = new ProjectsService();

export default class ProjectDetailsActions extends Actions<
  ProjectDetailsState,
  ProjectDetailsGetters,
  ProjectDetailsMutations,
  // eslint-disable-next-line no-use-before-define
  ProjectDetailsActions
> {
  async get_products_by_id(id: string | number) {
    try {
      const {
        sortBy = [], sortDesc = [], page = 1, itemsPerPage = 10,
      } = this.state.table_options;
      const payload: FiltersPagination<Filters> = {
        filters: this.state.filters,
        pagination: {
          limit: itemsPerPage,
          page,
          sort: '',
          sort_type: 'asc',
        },
      };

      if (sortBy?.length) {
        [payload.pagination.sort] = sortBy;
      }

      if (sortDesc?.length) {
        payload.pagination.sort_type = sortDesc[0] ? 'desc' : 'asc';
      }

      this.mutations.TOGGLE_PRODUCTS_LOADING(true);

      const { data, meta } = await projectsService.get_products_by_id(payload, id);

      this.mutations.SET_PRODUCTS(data.items);
      this.mutations.UPD_TABLE_OPTIONS({ key: 'itemsPerPage', value: meta.limit });
      this.mutations.UPD_TABLE_OPTIONS({ key: 'page', value: meta.current_page });
      this.mutations.UPD_PAGINATION_FIELD({ key: 'current_page', value: meta.current_page });
      this.mutations.UPD_PAGINATION_FIELD({ key: 'total_pages', value: meta.total_pages });
      this.mutations.UPD_PAGINATION_FIELD({ key: 'total_items', value: meta.total_items });
      this.mutations.UPD_PAGINATION_FIELD({ key: 'limit', value: meta.limit });
    } catch (error) {
      console.error(error);
    } finally {
      this.mutations.TOGGLE_PRODUCTS_LOADING(false);
    }
  }

  async get_equipments_by_id(id: string | number) {
    try {
      const {
        sortBy = [], sortDesc = [], page = 1, itemsPerPage = 10,
      } = this.state.equipments_table_options;
      const payload: FiltersPagination<Filters> = {
        filters: this.state.equipments_filters,
        pagination: {
          limit: itemsPerPage,
          page,
          sort: '',
          sort_type: 'asc',
        },
      };

      if (sortBy?.length) {
        [payload.pagination.sort] = sortBy;
      }

      if (sortDesc?.length) {
        payload.pagination.sort_type = sortDesc[0] ? 'desc' : 'asc';
      }

      this.mutations.TOGGLE_EQUIPMENTS_LOADING(true);

      const { data, meta } = await projectsService.get_equipments_by_id(payload, id);

      this.mutations.SET_EQUIPMENTS(data.items);
      this.mutations.UPD_EQUIPMENTS_TABLE_OPTIONS({ key: 'itemsPerPage', value: meta.limit });
      this.mutations.UPD_EQUIPMENTS_TABLE_OPTIONS({ key: 'page', value: meta.current_page });
      this.mutations.UPD_EQUIPMENTS_PAGINATION_FIELD({ key: 'current_page', value: meta.current_page });
      this.mutations.UPD_EQUIPMENTS_PAGINATION_FIELD({ key: 'total_pages', value: meta.total_pages });
      this.mutations.UPD_EQUIPMENTS_PAGINATION_FIELD({ key: 'total_items', value: meta.total_items });
      this.mutations.UPD_EQUIPMENTS_PAGINATION_FIELD({ key: 'limit', value: meta.limit });
    } catch (error) {
      console.error(error);
    } finally {
      this.mutations.TOGGLE_EQUIPMENTS_LOADING(false);
    }
  }

  async get_competitors_by_id(id: string | number) {
    try {
      const {
        sortBy = [], sortDesc = [], page = 1, itemsPerPage = 10,
      } = this.state.competitors_table_options;
      const payload: FiltersPagination<Filters> = {
        filters: this.state.competitors_filters,
        pagination: {
          limit: itemsPerPage,
          page,
          sort: '',
          sort_type: 'asc',
        },
      };

      if (sortBy?.length) {
        [payload.pagination.sort] = sortBy;
      }

      if (sortDesc?.length) {
        payload.pagination.sort_type = sortDesc[0] ? 'desc' : 'asc';
      }

      this.mutations.TOGGLE_COMPETITORS_LOADING(true);

      const { data, meta } = await projectsService.get_competitors_by_id(payload, id);

      this.mutations.SET_COMPETITORS(data.items);
      this.mutations.UPD_COMPETITORS_TABLE_OPTIONS({ key: 'itemsPerPage', value: meta.limit });
      this.mutations.UPD_COMPETITORS_TABLE_OPTIONS({ key: 'page', value: meta.current_page });
      this.mutations.UPD_COMPETITORS_PAGINATION_FIELD({ key: 'current_page', value: meta.current_page });
      this.mutations.UPD_COMPETITORS_PAGINATION_FIELD({ key: 'total_pages', value: meta.total_pages });
      this.mutations.UPD_COMPETITORS_PAGINATION_FIELD({ key: 'total_items', value: meta.total_items });
      this.mutations.UPD_COMPETITORS_PAGINATION_FIELD({ key: 'limit', value: meta.limit });
    } catch (error) {
      console.error(error);
    } finally {
      this.mutations.TOGGLE_COMPETITORS_LOADING(false);
    }
  }

  async get_history_by_id(id: string | number) {
    try {
      const {
        sortBy = [], sortDesc = [], page = 1, itemsPerPage = 10,
      } = this.state.history_table_options;
      const payload: FiltersPagination<Filters> = {
        filters: this.state.history_filters,
        pagination: {
          limit: itemsPerPage,
          page,
          sort: '',
          sort_type: 'asc',
        },
      };

      if (sortBy?.length) {
        [payload.pagination.sort] = sortBy;
      }

      if (sortDesc?.length) {
        payload.pagination.sort_type = sortDesc[0] ? 'desc' : 'asc';
      }

      this.mutations.TOGGLE_HISTORY_LOADING(true);

      const { data, meta } = await projectsService.get_history_by_id(payload, id);

      this.mutations.SET_HISTORY(data.items);
      this.mutations.UPD_HISTORY_TABLE_OPTIONS({ key: 'itemsPerPage', value: meta.limit });
      this.mutations.UPD_HISTORY_TABLE_OPTIONS({ key: 'page', value: meta.current_page });
      this.mutations.UPD_HISTORY_PAGINATION_FIELD({ key: 'current_page', value: meta.current_page });
      this.mutations.UPD_HISTORY_PAGINATION_FIELD({ key: 'total_pages', value: meta.total_pages });
      this.mutations.UPD_HISTORY_PAGINATION_FIELD({ key: 'total_items', value: meta.total_items });
      this.mutations.UPD_HISTORY_PAGINATION_FIELD({ key: 'limit', value: meta.limit });
    } catch (error) {
      console.error(error);
    } finally {
      this.mutations.TOGGLE_HISTORY_LOADING(false);
    }
  }

  async get_orders_realizations_by_id(id: string | number) {
    try {
      const {
        sortBy = [], sortDesc = [], page = 1, itemsPerPage = 10,
      } = this.state.realizations_table_options;
      const payload: FiltersPagination<Filters> = {
        filters: this.state.realizations_filters,
        pagination: {
          limit: itemsPerPage,
          page,
          sort: '',
          sort_type: 'asc',
        },
      };

      if (sortBy?.length) {
        [payload.pagination.sort] = sortBy;
      }

      if (sortDesc?.length) {
        payload.pagination.sort_type = sortDesc[0] ? 'desc' : 'asc';
      }

      this.mutations.TOGGLE_REALIZATIONS_LOADING(true);

      const { data, meta } = await projectsService.get_orders_realizations_by_id(payload, id);

      this.mutations.SET_ORDERS_REALIZATIONS(data.items);
      this.mutations.UPD_REALIZATIONS_TABLE_OPTIONS({ key: 'itemsPerPage', value: meta.limit });
      this.mutations.UPD_REALIZATIONS_TABLE_OPTIONS({ key: 'page', value: meta.current_page });
      this.mutations.UPD_REALIZATIONS_PAGINATION_FIELD({ key: 'current_page', value: meta.current_page });
      this.mutations.UPD_REALIZATIONS_PAGINATION_FIELD({ key: 'total_pages', value: meta.total_pages });
      this.mutations.UPD_REALIZATIONS_PAGINATION_FIELD({ key: 'total_items', value: meta.total_items });
      this.mutations.UPD_REALIZATIONS_PAGINATION_FIELD({ key: 'limit', value: meta.limit });
    } catch (error) {
      console.error(error);
    } finally {
      this.mutations.TOGGLE_REALIZATIONS_LOADING(false);
    }
  }

  async get_regions() {
    try {
      const { data } = await projectsService.get_regions();

      this.mutations.SET_REGIONS(data.items);
    } catch (error) {
      console.error(error);
    }
  }

  async checking_cart() {
    try {
      const { data } = await projectsService.checking_cart();

      this.mutations.SET_CART_IS_EMPTY(data.cart_is_empty);
    } catch (error) {
      console.error(error);
    }
  }

  async project_to_cart(payload: ProjectToCartRequest) {
    try {
      await projectsService.project_to_cart(payload);
    } catch (error) {
      console.error(error);
    }
  }
}
